import { PostgrestResponse } from '@supabase/supabase-js';
import { IVendas } from '../../../types/tables/IVendas';
import { BaseMultiTenantService } from '../base/BaseMultiTenantService';

/**
 * Serviço para gerenciamento de vendas com isolamento multi-tenant
 * Gerencia todo o processo de vendas da cantina
 */
export class VendasService extends BaseMultiTenantService<IVendas> {
     constructor() {
          super('vendas', ['numero_venda', 'observacoes'], 'fk_id_organizacao');
     }

     /**
      * Busca vendas de hoje - operação simples na tabela
      */
     async getVendasHoje(organizationId: number): Promise<PostgrestResponse<IVendas[]>> {
          const hoje = new Date().toISOString().split('T')[0];
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .gte('data_venda', hoje)
               .order('data_venda', { ascending: false });
     }

     /**
      * Busca vendas por período - operação simples na tabela
      */
     async getVendasPorPeriodo(organizationId: number, dataInicio: string, dataFim: string): Promise<PostgrestResponse<IVendas[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .gte('data_venda', dataInicio)
               .lte('data_venda', dataFim)
               .order('data_venda', { ascending: false });
     }

     /**
      * Busca vendas por aluno - operação simples na tabela
      */
     async getVendasPorAluno(organizationId: number, alunoId: number, limit?: number): Promise<PostgrestResponse<IVendas[]>> {
          let query = this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_aluno', alunoId)
               .order('data_venda', { ascending: false });

          if (limit) {
               query = query.limit(limit);
          }

          return await query;
     }

     /**
      * Busca vendas por responsável - operação simples na tabela
      */
     async getVendasPorResponsavel(organizationId: number, responsavelId: number, limit?: number): Promise<PostgrestResponse<IVendas[]>> {
          let query = this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('fk_id_responsavel', responsavelId)
               .order('data_venda', { ascending: false });

          if (limit) {
               query = query.limit(limit);
          }

          return await query;
     }

     /**
      * Busca vendas pendentes - operação simples na tabela
      */
     async getVendasPendentes(organizationId: number): Promise<PostgrestResponse<IVendas[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq(this.organizationIdField, organizationId)
               .eq('status_venda', 'pendente')
               .order('data_venda', { ascending: true });
     }

     /**
      * Processa uma nova venda
      */
     async processarVenda(
          organizationId: number,
          vendaData: {
               fk_id_aluno: number;
               fk_id_responsavel: number;
               fk_id_operador: number;
               itens: Array<{
                    fk_id_produto: number;
                    quantidade: number;
                    valor_unitario: number;
               }>;
               desconto?: number;
               observacoes?: string;
          },
          userId?: number,
     ): Promise<PostgrestResponse<IVendas[]>> {
          // Calcular valores
          const valorTotal = vendaData.itens.reduce((total, item) => total + item.quantidade * item.valor_unitario, 0);
          const desconto = vendaData.desconto || 0;
          const valorFinal = valorTotal - desconto;

          // Gerar número da venda
          const { data: ultimaVenda } = await this.client.from(this.tableName).select('numero_venda').eq(this.organizationIdField, organizationId).order('numero_venda', { ascending: false }).limit(1).maybeSingle();

          const numeroVenda = (ultimaVenda?.numero_venda || 0) + 1;

          // Criar venda
          const novaVenda = {
               numero_venda: numeroVenda,
               fk_id_aluno: vendaData.fk_id_aluno,
               fk_id_responsavel: vendaData.fk_id_responsavel,
               fk_id_operador: vendaData.fk_id_operador,
               data_venda: new Date().toISOString(),
               valor_total: valorTotal,
               desconto: desconto,
               valor_final: valorFinal,
               status_venda: 'pendente',
               observacoes: vendaData.observacoes,
          };

          const { data: venda, error: vendaError } = await this.create(novaVenda, organizationId, userId);

          if (vendaError) {
               throw new Error(`Erro ao criar venda: ${vendaError.message}`);
          }

          // Criar itens da venda
          const itensVenda = vendaData.itens.map((item) => ({
               fk_id_venda: venda.id,
               fk_id_produto: item.fk_id_produto,
               quantidade: item.quantidade,
               valor_unitario: item.valor_unitario,
               valor_total: item.quantidade * item.valor_unitario,
               desconto_item: 0,
          }));

          const { error: itensError } = await this.client.from('vendas_itens').insert(itensVenda);

          if (itensError) {
               // Reverter venda se falhar nos itens
               await this.remove(venda.id, organizationId);
               throw new Error(`Erro ao criar itens da venda: ${itensError.message}`);
          }

          return venda;
     }

     /**
      * Confirma retirada de produtos (finaliza venda)
      */
     async confirmarRetirada(id: number, organizationId: number, userId?: number): Promise<PostgrestResponse<IVendas[]>> {
          return await this.update(id, {
               status_venda: 'entregue' }, organizationId, userId);
          }

     /**
      * Cancela uma venda
      */
     async cancelarVenda(id: number, organizationId: number, motivo: string, userId ?: number): Promise < PostgrestResponse < IVendas[] >> {
               return await this.update(
                    id,
                    {
                         status_venda: 'cancelada',
                         observacoes: `CANCELADA: ${motivo}`,
                    },
                    organizationId,
                    userId,
               );
          }

     /**
      * Relatório de vendas por período - usar function do banco
      */
     async getRelatorioVendas(organizationId: number, dataInicio: string, dataFim: string): Promise < PostgrestResponse < IVendas[] >> {
               return await this.client.rpc('relatorio_vendas_periodo', {
                    p_organizacao_id: organizationId,
                    p_data_inicio: dataInicio,
                    p_data_fim: dataFim,
               });
          }

     /**
      * Dashboard de vendas - usar function do banco
      */
     async getDashboardVendas(organizationId: number): Promise < PostgrestResponse < IVendas[] >> {
               return await this.client.rpc('dashboard_vendas_hoje', {
                    p_organizacao_id: organizationId,
               });
          }
}


export const vendasService = new VendasService();
